import { HostName } from "pages/About/hosts";
import { Image } from "shared/types";

export type CategoryTag = string | string[]

export interface EpisodeResource {
  label: string;
  url?: string;
}

export interface EpisodeResourceGroup {
  tag: string;
  resources: EpisodeResource[]
}

export interface DiscussionPoint {
  title: string;
  description: string;
}

export interface ShowNotes {
  intro: string;
  quote?: {
    text: string;
    attribution: string;
  };
  discussionPoints: DiscussionPoint[];
}

export interface SyndicatedEpisode {
  buzzsproutId: number;
  episodeNumber?: number;
  bonus: boolean;
  title: string;
  description: string;
  publishedDate: Date;
  audioUrl: string;
  durationSeconds: number;
  artworkUrl?: string;
}

export interface EpisodeEntry {
  number: string;
  buzzsproutId?: number;
  title: string;
  description: string;
  image: Image;
  titleHighlight?: string;
  bonus?: boolean;
  host?: HostName;
  category?: CategoryTag;
  showNotes?: ShowNotes;
  resources?: EpisodeResourceGroup[];
  artworkUrl?: string;
}

export interface PublishedEpisode extends EpisodeEntry {
  buzzsproutId: number;
  host: HostName;
  category: CategoryTag;
  showNotes: ShowNotes;
  publishedDate: Date;
  audioUrl: string;
  durationSeconds: number;
  durationTimestamp: string;
}

export interface UpcomingEpisode extends EpisodeEntry {
  publishedDate?: never;
  audioUrl?: never;
}

export type Episode = UpcomingEpisode | PublishedEpisode;

export const isUpcomingEpisode = (episode: Episode): episode is UpcomingEpisode =>
  episode.buzzsproutId === undefined;

export const isPublishedEpisode = (episode: Episode): episode is PublishedEpisode =>
  episode.buzzsproutId !== undefined;

export const EPISODES: EpisodeEntry[] = [
  {
    number: '2',
    host: "Liz",
    category: ["Folklore", "Mythology"],
    image: {
      src: '/images/episodes/2-not-the-bees.jpg',
      alt: 'A swarm of bees on a galactic balcony.'
    },
    title: "Not the Bees!",
    titleHighlight: "Bees!",
    description:
      "Bees are bee-u-tiful, wonderful and important insects. But did you know that there’s a lot more to them from a myth stand point?",
    showNotes: {
      intro:
        "Bees are beneficial part of nature, that help keep our world flourishing. There’s a little more to these sweeties than meets the eye. For today’s Moms Myths and Mimosas, Liz talks about the stories from history about bees. Then the moms talk about sleep struggles and recent milestones.",
      quote: {
        text: "Bees? Bees? BEES?!",
        attribution: "AJ",
      },
      discussionPoints: [
        {
          title: "Bee Basics",
          description:
            "A quick primer on what bees actually are, the social structure of a hive, and a surprising defense mechanism.",
        },
        {
          title: "Telling It to the Bees",
          description:
            "The old tradition of treating a hive like family. Beekeeper etiquette, and the close bond between beekeepers and their bees.",
        },
        {
          title: "Mythology of Bees",
          description:
            "From a Bee's bargain with Zeus, to the Egyptians using honey to embalm their dead, and the Celtic laws so strict that stealing a hive was a capital offence. Bees have been revered across millennia.",
        },
        {
          title: "How to Help the Bees",
          description:
            "Practical things you can do to be more bee-friendly. From \"bee baths\" to buying local.",
        },
        {
          title: "Mom Chats: All Things Sleep",
          description:
            "Our own sleep (or lack of it), our kids' sleep struggles and milestones, and the funny anecdotes we couldn't leave out.",
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'Honey Bee Legends, Myths, and Folklore',
            url: 'https://www.farmersalmanac.com/honey-bee-legends',
          },
          {
            label: 'Why Bees Have Become Cultural Icons: From Mythology to Modern Trends',
            url: 'https://hivekeepers.com/blogs/news/why-bees-have-become-cultural-icons-from-mythology-to-modern-trends',
          },
          {
            label: 'Bee Folklore for Dark Days',
            url: 'https://www.beepods.com/bee-folklore-for-dark-days/',
          },
          {
            label: 'Mythos by Stephen Fry',
            url: "https://www.goodreads.com/book/show/35074096-mythos"
          },
        ],
      },
    ],
  },
  {
    number: 'easter-bonus',
    host: 'AJ',
    bonus: true,
    category: ['Bonus', 'Folklore'],
    image: {
      src: '/images/episodes/easter-bonus-on-the-hunt-for-the-easter-bunny.png',
      alt: 'A bunny sits in a magical forest surrounded by eggs that are covered in cryptic text'
    },
    title: 'On the Hunt for the Easter Bunny',
    titleHighlight: 'Easter Bunny',
    description:
      'What do bunnies and eggs have to do with Easter? Let\'s explore some Easter traditions and their origins',
    showNotes: {
      intro:
        'Is Easter actually named for the goddess Ishtar? What do bunnies and eggs have to do with it? Let\'s dig into the murky origins of the holiday\'s name, question its pagan roots, and trace how a rabbit hiding eggs somehow became an iconic tradition. All while sharing some of our own favourite childhood Easter memories, and our plans for this year.',
      quote: {
        text: 'It\'s also a good reminder to not blindly believe everything you read on the internet.',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Where did Easter get its name?',
          description:
            'Online claims of pagan origins attributed to multiple different goddesses, but which one is true?',
        },
        {
          title: "The Easter bunny and its colourful eggs",
          description:
            'What does a bunny have to do with Easter? Why do we give children colourful eggs and chocolate?',
        },
        {
          title: "The Easter... Bilby?",
          description:
            'Australia has chosen to eschew the Easter Bunny in favour of a more local wildlife icon'
        },
        {
          title: 'Easter plans and childhood memories',
          description:
            'We talk about how we\'re making Easter special for our children, while reminiscing about what we loved from our own childhoods.',
        },
      ],
    },
    resources: [
      {
        tag: 'Read',
        resources: [
          {
            label: 'Easter, Ishtar, Eoster and Eggs',
            url: 'https://historyforatheists.com/2017/04/easter-ishtar-eostre-and-eggs/'
          },
          {
            label: 'Eosturmonath (April)',
            url: 'https://www.carlanayland.org/essays/eosturmonath.htm'
          },
          {
            label: 'On the Bunny Trail: In Search of the Easter Bunny',
            url: 'https://blogs.loc.gov/folklife/2016/03/easter-bunny/'
          },
          {
            label: 'Easter Bilby',
            url: 'https://en.wikipedia.org/wiki/Easter_Bilby'
          },
        ]
      }
    ],
  },
  {
    number: '1',
    buzzsproutId: 19035086,
    host: 'Liz',
    category: 'Cryptids',
    image: {
      src: '/images/episodes/1-lets-get-kraken.png',
      alt: 'The Kraken has emerged from the watery depths of the ocean clutching a chest'
    },
    title: 'Let\'s Get Kraken',
    titleHighlight: 'Kraken',
    description:
      'In our inaugural episode, our trio of hero mamas discuss the most legendary monster of the seas, the Kraken.',
    showNotes: {
      intro:
        'What lurks beneath the waves? In our very first episode, join us while we dive into the origins of the Kraken. From twisted Scandinavian folklore to dying giant squids flailing at the surface. We explore how Norse sailors turned real ocean encounters into terrifying legends, and what modern marine biology has to say about it all. After get to know the hosts a bit while we discuss the origin of our own motherhood stories, pregnancy!',
      quote: {
        text: 'Whales are sea monsters.',
        attribution: 'Kelly',
      },
      discussionPoints: [
        {
          title: 'Origins of the Kraken',
          description:
            'From the Scandinavian word "krake" meaning twisted or sickly, to Norse legends of a colossal squid-like beast that could drag entire ships to the ocean floor.',
        },
        {
          title: 'The Giant Squid Theory',
          description:
            'Marine biologist in.vertebabe explains how dying giant squids fill with ammonia, rise to the surface in a delirious state, and flail into ships creating instant Kraken sightings.',
        },
        {
          title: 'Fishermen\'s Friend or Foe?',
          description:
            'Not every Kraken encounter was a disaster, schools of fish would rise to the surface in its wake, giving brave fishermen a massive catch if they survived.',
        },
        {
          title: 'Pregnancy Stories',
          description:
            'Three very different journeys into motherhood: meticulously planned, "if it happens, it happens," and a complete surprise. Plus, how we each broke the news to our partners.',
        },
      ],
    },
    // resources: [
    //   {
    //     tag: 'Watch',
    //     resources: [
    //       { label: 'in.vertebabe on TikTok: Giant Squid and the Kraken' },
    //     ]
    //   },
    //   {
    //     tag: 'Read',
    //     resources: [
    //       { label: 'The Kraken in Scandinavian Folklore' },
    //       { label: 'Scylla and Charybdis in Greek Mythology' },
    //     ]
    //   },
    // ],
  },
];
